import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type {
  PluginConfigurationError,
  PluginConfigurationField,
  PluginConfigurationSchema,
  PluginConfigurationValues,
} from '../../api/types';
import { pluginKey } from '../../i18n';

interface Props {
  pluginId: string;
  schema: PluginConfigurationSchema;
  values: PluginConfigurationValues;
  /// Secret fields that already hold a stored value. Their inputs stay empty; leaving one
  /// empty keeps what is stored, which is why the server is never sent the old value back.
  secretsWithValue: string[];
  errors: PluginConfigurationError[];
  onChange: (values: PluginConfigurationValues) => void;
  disabled?: boolean;
}

/// Renders a plugin's settings from the schema it declared. Nothing here knows what any
/// particular plugin's settings mean - it knows field kinds, and the server checks the result
/// again before storing it.
export default function PluginConfigurationForm({
  pluginId,
  schema,
  values,
  secretsWithValue,
  errors,
  onChange,
  disabled,
}: Props) {
  const { t } = useTranslation();

  const label = (key: string) => t(pluginKey(pluginId, key));
  const optionalLabel = (key: string | null) => (key === null ? undefined : label(key));

  const errorAt = (path: string) => errors.find((error) => error.path === path);

  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value });

  return (
    <Stack spacing={3}>
      {schema.sections.map((section) => (
        <Card key={section.key} variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {label(section.titleKey)}
            </Typography>

            {section.descriptionKey && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {label(section.descriptionKey)}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack spacing={2.5}>
              {section.fields.map((field) => (
                <Field
                  key={field.key}
                  pluginId={pluginId}
                  field={field}
                  path={field.key}
                  value={values[field.key]}
                  secretHasValue={secretsWithValue.includes(field.key)}
                  errors={errors}
                  onChange={(value) => set(field.key, value)}
                  disabled={disabled}
                  label={label}
                  optionalLabel={optionalLabel}
                  errorAt={errorAt}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

interface FieldProps {
  pluginId: string;
  field: PluginConfigurationField;
  /// Where this value sits in the document, in the same form the server reports failures at:
  /// `roots`, `roots[0]`, `proxy.host`.
  path: string;
  value: unknown;
  secretHasValue?: boolean;
  errors: PluginConfigurationError[];
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

interface Helpers {
  label: (key: string) => string;
  optionalLabel: (key: string | null) => string | undefined;
  errorAt: (path: string) => PluginConfigurationError | undefined;
}

function Field({
  pluginId,
  field,
  path,
  value,
  secretHasValue,
  errors,
  onChange,
  disabled,
  label,
  optionalLabel,
  errorAt,
}: FieldProps & Helpers) {
  const { t } = useTranslation();

  const failure = errorAt(path);
  const description = optionalLabel(field.descriptionKey);

  // A failure replaces the description: the operator needs to read what is wrong, not what the
  // field is for, and the description is one line above in the form anyway.
  const helperText = failure ? t(failure.code, { defaultValue: failure.message }) : description;

  const common = {
    fullWidth: true,
    size: 'small' as const,
    disabled,
    error: failure !== undefined,
    helperText,
    label: label(field.labelKey),
    required: field.isRequired,
    placeholder: optionalLabel(field.placeholderKey),
  };

  switch (field.kind) {
    case 'Boolean':
      return (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={value === true}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
              />
            }
            label={label(field.labelKey)}
          />
          {helperText && (
            <Typography variant="caption" color={failure ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
              {helperText}
            </Typography>
          )}
        </Box>
      );

    case 'WholeNumber':
    case 'Number':
      return (
        <TextField
          {...common}
          type="number"
          value={value ?? ''}
          slotProps={{
            htmlInput: {
              min: field.minimum ?? undefined,
              max: field.maximum ?? undefined,
              step: field.kind === 'WholeNumber' ? 1 : 'any',
            },
          }}
          onChange={(event) => {
            const raw = event.target.value;
            onChange(raw === '' ? undefined : Number(raw));
          }}
        />
      );

    case 'Secret':
      // An untouched secret is one this form never reported a value for, and the server keeps
      // what it has. Typing replaces it; clearing it explicitly is what removes it, which is
      // why there is a button rather than an empty field meaning two different things.
      return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            {...common}
            type="password"
            autoComplete="new-password"
            value={typeof value === 'string' ? value : ''}
            helperText={
              failure
                ? helperText
                : [description, secretHasValue && value === undefined ? t('plugins.secretStored') : undefined]
                    .filter(Boolean)
                    .join(' ')
            }
            onChange={(event) => onChange(event.target.value)}
          />

          {secretHasValue && value === undefined && (
            <Button size="small" disabled={disabled} onClick={() => onChange('')} sx={{ mt: 0.5 }}>
              {t('common.remove')}
            </Button>
          )}
        </Stack>
      );

    case 'Select':
      return (
        <TextField {...common} select value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
          {field.choices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {label(choice.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      );

    case 'MultilineText':
      return (
        <TextField
          {...common}
          multiline
          minRows={3}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'Group':
      return (
        <Box sx={{ borderInlineStart: 2, borderColor: 'divider', paddingInlineStart: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label(field.labelKey)}
          </Typography>

          {helperText && (
            <Typography variant="caption" color={failure ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 1.5 }}>
              {helperText}
            </Typography>
          )}

          <Stack spacing={2}>
            {field.fields.map((child) => (
              <Field
                key={child.key}
                pluginId={pluginId}
                field={child}
                path={`${path}.${child.key}`}
                value={(value as Record<string, unknown> | undefined)?.[child.key]}
                errors={errors}
                disabled={disabled}
                label={label}
                optionalLabel={optionalLabel}
                errorAt={errorAt}
                onChange={(child_value) =>
                  onChange({ ...((value as Record<string, unknown>) ?? {}), [child.key]: child_value })
                }
              />
            ))}
          </Stack>
        </Box>
      );

    case 'List':
      return (
        <ListField
          pluginId={pluginId}
          field={field}
          path={path}
          value={value}
          errors={errors}
          onChange={onChange}
          disabled={disabled}
          label={label}
          optionalLabel={optionalLabel}
          errorAt={errorAt}
        />
      );

    default:
      // Text, Url, Host, Directory, FilePath and Duration are all a line of text; what makes
      // them different is the rule the server applies, not the input.
      return (
        <TextField
          {...common}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/// Any number of entries, either single values or groups of fields. The empty state says so
/// rather than showing a bare "Add" button with nothing above it.
function ListField({
  pluginId,
  field,
  path,
  value,
  errors,
  onChange,
  disabled,
  label,
  optionalLabel,
  errorAt,
}: FieldProps & Helpers) {
  const { t } = useTranslation();

  const entries = Array.isArray(value) ? value : [];
  const failure = errorAt(path);
  const description = optionalLabel(field.descriptionKey);
  const itemField = field.itemField;
  const isGroupList = itemField === null;

  const replace = (index: number, entry: unknown) =>
    onChange(entries.map((existing, position) => (position === index ? entry : existing)));

  const add = () => onChange([...entries, isGroupList ? {} : '']);

  const remove = (index: number) => onChange(entries.filter((_, position) => position !== index));

  const atMaximum = field.maximumItems !== null && entries.length >= field.maximumItems;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label(field.labelKey)}
      </Typography>

      {(failure || description) && (
        <Typography variant="caption" color={failure ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 1 }}>
          {failure ? t(failure.code, { defaultValue: failure.message }) : description}
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('plugins.listEmpty')}
          </Typography>
        )}

        {entries.map((entry, index) => (
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {isGroupList ? (
                <Stack spacing={2}>
                  {field.fields.map((child) => (
                    <Field
                      key={child.key}
                      pluginId={pluginId}
                      field={child}
                      path={`${path}[${index}].${child.key}`}
                      value={(entry as Record<string, unknown> | undefined)?.[child.key]}
                      errors={errors}
                      disabled={disabled}
                      label={label}
                      optionalLabel={optionalLabel}
                      errorAt={errorAt}
                      onChange={(child_value) =>
                        replace(index, { ...((entry as Record<string, unknown>) ?? {}), [child.key]: child_value })
                      }
                    />
                  ))}
                </Stack>
              ) : (
                <Field
                  pluginId={pluginId}
                  field={{ ...itemField, key: field.key }}
                  path={`${path}[${index}]`}
                  value={entry}
                  errors={errors}
                  disabled={disabled}
                  label={label}
                  optionalLabel={optionalLabel}
                  errorAt={errorAt}
                  onChange={(entry_value) => replace(index, entry_value)}
                />
              )}
            </Box>

            <IconButton
              aria-label={t('common.remove')}
              disabled={disabled}
              onClick={() => remove(index)}
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}

        <Box>
          <Button size="small" startIcon={<AddIcon />} disabled={disabled || atMaximum} onClick={add}>
            {t('common.add')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
